import { useEffect, useState } from "react";
import { Link, router } from "@inertiajs/react";
import { usePage } from "@inertiajs/react";
import NavMenu from "@components/nav_menu.tsx";
import GreenhouseLogo from "@images/greenhouse_logo.svg";
import GreenhouseIconDarkGreen from "@images/greenhouse_icon_dark_green.svg";
import GreenhouseIconWhite from "@images/greenhouse_icon_white.svg";
import HamburgerIcon from "@sprout/icons/hamburger.tsx";
import CloseIcon from "@sprout/icons/close.tsx";
import "./header.scss";
import useTranslation from "@hooks/use_translation.ts";
import useDevice from "@hooks/use_device.ts";
import { Row } from "@sprout/layout.tsx";
import CandidateIcon from "@sprout/icons/candidate.tsx";
import SupportDialog from "@components/support_dialog.tsx";
import { Tag } from "@/sprout/tag";
import axios from "axios";

type PageProps = {
  url: string;
  isJobSearchEnabled: boolean;
  emailPreferencesPath?: string;
};

type Props = {
  paths?: {
    dashboard: string;
    applications: string;
    jobSearch: string;
    profile: string;
    reportBugPath: string;
    signOut: string;
  };
};

const Header = ({
  paths = {
    dashboard: "/dashboard",
    applications: "/applications",
    jobSearch: "/jobs",
    profile: "/profile",
    reportBugPath: "/users/report_bug",
    signOut: "/users/sign_out",
  },
}: Props) => {
  const { url, isJobSearchEnabled, emailPreferencesPath } = usePage<PageProps>().props;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isSupportDialogOpen, setIsSupportDialogOpen] = useState(false);
  const device = useDevice();

  const { t } = useTranslation("misc");

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
    }
  }, [mobileMenuOpen]);

  const logOut = async () => {
    setMobileMenuOpen(false);
    localStorage.removeItem("JOB_SEEKERS_REACT_QUERY_CACHE");

    try {
      await axios.delete(paths.signOut);
    } catch (e) {
      console.error(e);
    } finally {
      // reload the page to clear the session
      window.location.href = "/";
    }
  };

  const renderLogo = () => {
    if (device.isMobile) {
      const MobileIcon = mobileMenuOpen ? GreenhouseIconWhite : GreenhouseIconDarkGreen;
      return (
        <Link href="/" className="header__logo-wrapper">
          <img src={MobileIcon} alt="Greenhouse icon" />
        </Link>
      );
    }

    return (
      <Link href="/" className="header__logo-wrapper">
        <img src={GreenhouseLogo} alt="Greenhouse logo" />
      </Link>
    );
  };

  const renderLinks = () => (
    <>
      <Link
        href={paths.dashboard}
        onClick={() => setMobileMenuOpen(false)}
        className={`header__item ${url === paths.dashboard ? "selected" : ""}`}
      >
        {t("home")}
      </Link>
      <Link
        href={paths.applications}
        onClick={() => setMobileMenuOpen(false)}
        className={`header__item ${url === paths.applications ? "selected" : ""}`}
      >
        {t("applications")}
      </Link>
      {isJobSearchEnabled && (
        <a
          href={paths.jobSearch}
          onClick={() => setMobileMenuOpen(false)}
          className={`header__item ${url === paths.jobSearch ? "selected" : ""}`}
        >
          {t("jobSearch")}
        </a>
      )}
      {!isJobSearchEnabled && (
        <Row gap="8px" align="center">
          <span
            aria-disabled={true}
            className="header__item header__item--disabled whitespace-nowrap cursor-pointer"
          >
            {t("jobSearch")}
          </span>
          <Tag label={t("comingSoon")} />
        </Row>
      )}
      {device.isMobile && mobileMenuOpen && (
        <>
          <Link
            href={paths.profile}
            onClick={() => setMobileMenuOpen(false)}
            className={`header__item ${url === paths.profile ? "selected" : ""}`}
          >
            {t("profile")}
          </Link>
          {emailPreferencesPath && (
            <Link
              href={emailPreferencesPath}
              onClick={() => setMobileMenuOpen(false)}
              className="header__item"
            >
              {t("notifications")}
            </Link>
          )}
          <button className="header__item" onClick={logOut}>
            {t("logOut")}
          </button>
        </>
      )}
    </>
  );

  const renderDesktopMenuButtons = () => {
    return (
      <div className="header__desktop-menu-buttons">
        <NavMenu
          icon={<CandidateIcon />}
          items={[
            { value: 0, label: t("profile"), onSelect: () => router.visit(paths.profile) },
            ...(emailPreferencesPath
              ? [
                  {
                    value: 1,
                    label: t("notifications"),
                    onSelect: () => router.visit(emailPreferencesPath),
                  },
                ]
              : []),
            { value: 2, label: t("logOut"), onSelect: logOut },
          ]}
        />
      </div>
    );
  };

  const renderMobileMenuButton = () => (
    <button
      className="header__mobile-menu-button"
      onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
    >
      {mobileMenuOpen ? (
        <CloseIcon size={"md"} color={"white"} />
      ) : (
        <HamburgerIcon size={"md"} color={"dark-green"} />
      )}
    </button>
  );

  return (
    <div className="z-1">
      <header className={`header ${mobileMenuOpen ? "header__mobile-menu-open" : ""}`}>
        <div className="header__content">
          <div className="header__left whitespace-nowrap">
            {renderLogo()}
            {device.isWeb && renderLinks()}
          </div>
          {device.isWeb && renderDesktopMenuButtons()}
          {device.isMobile && renderMobileMenuButton()}
        </div>
        {device.isMobile && mobileMenuOpen && (
          <div className="header__mobile-menu">{renderLinks()}</div>
        )}
        <SupportDialog
          isOpen={isSupportDialogOpen}
          setIsDialogOpen={setIsSupportDialogOpen}
          bugReportPath={paths.reportBugPath}
        />
      </header>
    </div>
  );
};

export default Header;
